import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Users, MapPin, Check, ArrowLeft, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PackageDetail = () => {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const response = await axios.get(`${API}/packages/${id}`);
        setPkg(response.data);
      } catch (error) {
        console.error('Error fetching package:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPackage();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-maroon text-xl">Loading...</p>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-maroon text-xl mb-4">Package not found</p>
          <Link to="/packages" className="btn-primary">Back to Packages</Link>
        </div>
      </div>
    );
  }

  const depositAmount = (pkg.price * 0.05).toFixed(2);

  return (
    <div className="py-16" data-testid="package-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/packages" className="inline-flex items-center space-x-2 text-maroon hover:text-terracotta mb-8">
          <ArrowLeft size={20} />
          <span className="font-semibold">Back to Packages</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          <div>
            <img 
              src={pkg.image_url} 
              alt={pkg.title}
              className="w-full aspect-[4/3] object-cover rounded-sm border-2 border-maroon hard-shadow"
            />
          </div>
          
          <div>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-maroon mb-4" data-testid="package-title">
              {pkg.title}
            </h1>
            <p className="text-xl text-maroon/70 mb-6">{pkg.description}</p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center space-x-2 text-maroon">
                <MapPin size={20} />
                <span className="font-semibold">{pkg.destination}</span>
              </div>
              <div className="flex items-center space-x-2 text-maroon">
                <Calendar size={20} />
                <span className="font-semibold">{pkg.duration}</span>
              </div>
              <div className="flex items-center space-x-2 text-maroon">
                <Users size={20} />
                <span className="font-semibold">{pkg.available_spots} spots available</span>
              </div>
              <div className="text-maroon">
                <span className="text-sm">Starts:</span>
                <p className="font-semibold">{pkg.start_date}</p>
              </div>
            </div>
            
            <div className="bg-sand/30 p-6 rounded-sm border border-maroon/10 mb-6">
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-3xl font-heading font-bold text-maroon mb-1">${pkg.price}</p>
                  <p className="text-sm text-maroon/70">per person</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-maroon/60">Reserve with 5% deposit</p>
                  <p className="text-xl font-bold text-terracotta">${depositAmount}</p>
                </div>
              </div>
            </div>
            
            <Link 
              to={`/book/${pkg.id}`} 
              className="btn-primary w-full flex items-center justify-center gap-2 text-lg py-4"
              data-testid="reserve-now-btn"
            >
              <CreditCard size={20} />
              Reserve Now - Pay ${depositAmount} Deposit
            </Link>
            
            <p className="text-center text-sm text-maroon/60 mt-3">
              <Link to="/terms#refund-policy" className="text-sm text-maroon/70 hover:text-terracotta underline">
                View our refund policy
              </Link>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="card">
            <h2 className="text-2xl font-heading font-bold text-maroon mb-4">Highlights</h2>
            <ul className="space-y-3">
              {pkg.highlights.map((highlight, idx) => (
                <li key={idx} className="flex items-start space-x-3">
                  <Check size={20} className="text-terracotta flex-shrink-0 mt-1" />
                  <span className="text-maroon/80">{highlight}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="card">
            <h2 className="text-2xl font-heading font-bold text-maroon mb-4">What's Included</h2>
            <ul className="space-y-3">
              {pkg.included.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-3">
                  <Check size={20} className="text-terracotta flex-shrink-0 mt-1" />
                  <span className="text-maroon/80">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageDetail;